
import { useEffect, useRef } from 'react';

export default function CostComparison() {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<any>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    import('chart.js/auto')
      .then((ChartJS) => {
        const Chart = ChartJS.default;

        chartInstanceRef.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: ['In-House Employee (1.5 Years)', 'Dinari Designs (1.5 Years)'],
            datasets: [
              {
                label: 'Total Cost (CAD)',
                data: [198000, 75000],
                backgroundColor: ['#EF4444', '#14B8A6'],
                borderColor: ['#DC2626', '#0D9488'],
                borderWidth: 2,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: function (context) {
                    return '$' + context.parsed.y.toLocaleString() + ' CAD';
                  },
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: function (value) {
                    return '$' + value.toLocaleString();
                  },
                },
              },
            },
          },
        });
      })
      .catch((err) => {
        console.error('Failed to load Chart.js:', err);
      });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <section id="cost-savings" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border-b-4 border-blue-600">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-12 text-center text-gray-900">
            The Proof: $123,000+ in True Cost Avoidance
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="text-2xl font-bold mb-6 text-blue-600">
                Total Cost of Employment (TCE) Breakdown
              </h3>
              <p className="text-lg text-gray-700 mb-8">
                The Dinari Designs square-footage-based fee is <strong>less than half</strong> the direct salary of an in-house employee, even before calculating mandatory BC contributions and operational overhead.
              </p>

              <div className="space-y-6">
                <div className="bg-red-50 rounded-lg p-6 border-l-4 border-red-500">
                  <h4 className="text-xl font-bold text-red-700 mb-4">
                    Total In-House Cost (1.5 Years): $198,000
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex justify-between">
                      <span>Base Salary (18 months):</span>
                      <strong>$112,500</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Benefits (25%):</span>
                      <strong>$28,125</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Payroll Taxes &amp; CPP/EI:</span>
                      <strong>$7,875</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Equipment &amp; Software:</span>
                      <strong>$12,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Training &amp; Development:</span>
                      <strong>$6,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Office Space &amp; Overhead:</span>
                      <strong>$9,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Recruitment &amp; HR:</span>
                      <strong>$4,500</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Sick Days &amp; Vacation:</span>
                      <strong>$4,500</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Management &amp; Supervision:</span>
                      <strong>$13,500</strong>
                    </li>
                    <li className="flex justify-between border-t-2 border-red-300 pt-3 text-lg font-bold text-red-700">
                      <span>Total Cost (1.5 Years):</span>
                      <span>$198,000</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-teal-50 rounded-lg p-6 border-l-4 border-teal-600">
                  <h4 className="text-xl font-bold text-teal-700 mb-4">
                    Dinari Designs Fee (Sq. Ft.): $75,000
                  </h4>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex justify-between">
                      <span>Service Fee (1.5 Years):</span>
                      <strong>$75,000</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Benefits:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Payroll Taxes:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Equipment &amp; Software:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Training:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Office Space:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>HR Management:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between">
                      <span>Downtime Coverage:</span>
                      <strong>$0</strong>
                    </li>
                    <li className="flex justify-between border-t-2 border-teal-300 pt-3 text-lg font-bold text-teal-700">
                      <span>Total Cost (1.5 Years):</span>
                      <span>$75,000</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-6 text-white text-center">
                  <p className="text-lg mb-2">Your Total Savings (1.5 Years):</p>
                  <p className="text-4xl font-extrabold">$123,000</p>
                  <p className="text-sm mt-2 text-teal-100">
                    Plus eliminated risk and guaranteed quality
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h4 className="text-xl font-bold text-gray-900 mb-6 text-center">
                  Cost Comparison (1.5 Years)
                </h4>
                <div className="h-80">
                  <canvas ref={chartRef}></canvas>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <h4 className="text-xl font-bold text-blue-900 mb-4">
                  Hidden Costs You Avoid:
                </h4>
                <ul className="space-y-3 text-blue-800">
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>
                      <strong>No Rework Costs:</strong> Our 20+ years of experience
                      prevents costly field errors
                    </span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>
                      <strong>No Schedule Delays:</strong> Accurate detailing keeps
                      projects on track
                    </span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>
                      <strong>24/7 Availability:</strong> No sick days, vacations,
                      or turnover disruptions
                    </span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>
                      <strong>Scalable Service:</strong> Ramp up or down based on
                      project needs
                    </span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-teal-600 mr-3 mt-1 text-xl"></i>
                    <span>
                      <strong>Zero HR Burden:</strong> No hiring, training, or
                      management overhead
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
